import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { FEATURED_TIRES } from "../lib/constants";

export const FeaturedTires = ({ sidebar = null }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    const id = setInterval(() => emblaApi.scrollNext(), 4500);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section
      data-testid="featured-tires-section"
      id="pneus-destaque"
      className="relative pt-28 pb-14 md:pt-32 md:pb-16 bg-[#0E2452] border-y border-[#2F568F]"
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className={sidebar ? "max-w-7xl mx-auto" : "max-w-5xl mx-auto"}>
          {/* Heading */}
          <div className="text-center mb-8 max-w-3xl mx-auto">
            <span className="section-label">// Pneus em Destaque</span>
            <h2 className="heading-lg text-white mt-3 mb-3">
              Os pneus que <span className="text-[#5FA8FF]">recomendamos.</span>
            </h2>
            <p className="text-[#A1A1AA] font-body text-sm leading-relaxed mb-2">
              Uma seleção dos modelos mais procurados na nossa oficina —
              equilíbrio entre desempenho, segurança e preço. Disponíveis para
              montagem rápida no mesmo dia.
            </p>
            <p className="text-xs text-[#A1A1AA] font-body">
              * Para outras medidas e marcas, peça-nos um orçamento personalizado.
            </p>
          </div>

          <div className={sidebar ? "grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch" : ""}>
          {/* Carousel centered */}
          <div
            data-testid="featured-tires-carousel"
            className="relative rounded-2xl overflow-hidden border border-[#2F568F] bg-[#163A6D] mx-auto w-full"
          >
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex">
                  {FEATURED_TIRES.map((tire, idx) => (
                    <div
                      key={tire.brand + tire.model}
                      className="flex-[0_0_100%] min-w-0 px-6"
                      data-testid={`featured-tire-slide-${idx}`}
                    >
                      <div className="flex flex-col items-center text-center py-6 md:py-8 px-4">
                        <div className="w-full max-w-[260px] aspect-square flex items-center justify-center mb-5">
                          <img
                            src={tire.image}
                            alt={`${tire.brand} ${tire.model}`}
                            className="max-h-full max-w-full object-contain drop-shadow-[0_25px_50px_rgba(0,0,0,0.6)]"
                          />
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] font-display font-bold mb-2">
                          {tire.brand}
                        </span>
                        <div className="font-display font-bold text-white text-2xl md:text-3xl uppercase tracking-wide mb-1.5">
                          {tire.size}
                        </div>
                        <div className="text-sm text-[#A1A1AA] font-body mb-4">
                          {tire.model}
                        </div>

                        {tire.description && (
                          <p className="max-w-xl text-sm text-[#E0E0E0] font-body leading-relaxed mb-4">
                            {tire.description}
                          </p>
                        )}

                        {tire.highlights && tire.highlights.length > 0 && (
                          <ul
                            data-testid={`featured-tire-highlights-${idx}`}
                            className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-5 gap-y-2 max-w-3xl"
                          >
                            {tire.highlights.map((h) => (
                              <li
                                key={h}
                                className="flex items-center gap-2 text-xs md:text-sm font-display font-medium uppercase tracking-wide text-white"
                              >
                                <span className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center bg-[#FF5A00]">
                                  <Check className="w-3 h-3 text-black" strokeWidth={3} />
                                </span>
                                {h}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arrows */}
              <button
                type="button"
                data-testid="featured-tires-prev"
                onClick={() => emblaApi && emblaApi.scrollPrev()}
                aria-label="Pneu anterior"
                className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 rounded-full bg-[#0E2452]/80 hover:bg-[#FF5A00] border border-[#2F568F] hover:border-[#FF5A00] text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                data-testid="featured-tires-next"
                onClick={() => emblaApi && emblaApi.scrollNext()}
                aria-label="Pneu seguinte"
                className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 rounded-full bg-[#0E2452]/80 hover:bg-[#FF5A00] border border-[#2F568F] hover:border-[#FF5A00] text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {/* Dots */}
              <div className="flex items-center justify-center gap-1.5 pb-4">
                {FEATURED_TIRES.map((t, i) => (
                  <button
                    key={t.brand + t.model}
                    data-testid={`featured-tires-dot-${i}`}
                    onClick={() => emblaApi && emblaApi.scrollTo(i)}
                    aria-label={`Pneu ${i + 1}`}
                    className={`h-1 transition-all duration-300 ${
                      i === selectedIndex
                        ? "w-8 bg-[#FF5A00]"
                        : "w-3 bg-white/30 hover:bg-white/60"
                    }`}
                  />
                ))}
              </div>
            </div>

            {sidebar && (
              <div className="flex flex-col">
                {sidebar}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedTires;
